import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Button,
    Input,
    FormField,
    Label,
    ModalBody,
    ModalFooter,
    ModalHeader,
    TextArea,
    FieldErrorList,
    Modal
} from 'common/components';
import { ButtonVariant, CategoryField, InputType } from 'common/enums';
import { FieldError } from 'common/models';
import {
    ACTION_ITEMS,
    SUB_CATEGORY_DESCRIPTION_MAX_LENGTH,
    SUB_CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR,
    SUB_CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR,
    SUB_CATEGORY_NAME_INVALID_CHAR_ERROR,
    SUB_CATEGORY_NAME_MAX_LENGTH,
    SUB_CATEGORY_NAME_MAX_LENGTH_ERROR,
    SUB_CATEGORY_NAME_EMPTY_ERROR,
    SUB_CATEGORY_ORDER_EMPTY_ERROR,
    SUB_CATEGORY_ORDER_GREATER_THAN_ZERO,
    SUB_CATEGORY_ORDER_INVALID_ERROR,
    SUB_CATEGORY_ORDER_MAX_LENGTH,
    SUB_CATEGORY_ORDER_MAX_LENGTH_ERROR
} from 'common/config';
import { addValidationError, removeValidationError } from 'common/utils/error';
import { hasValue } from 'common/utils/data';
import { RootState } from 'common/redux/core/root.reducer';
import { validateInputString, validateMaxLength, validateNumbers } from 'common/utils/validator';
import { EntityDetailsPayload } from 'common/models/data/entityStore.model';
import { entityActions } from 'common/redux/entity';
import { getCategoryDetailsForEdit } from 'common/redux/entity/entity.selector';
import { CategoryDTO } from 'common/models/data/entity.model';

import './SubCategoryEdit.scss';

type SubCategoryEditProps = {
    data: EntityDetailsPayload;
    fieldErrors: Array<FieldError>;
    onClose: () => void;
    onUpdate: (updatedData: CategoryDTO) => void;
    updateFieldErrors: (errors: Array<FieldError>) => void;
    isEditModalVisible: boolean;
};

const SubCategoryEdit: FC<SubCategoryEditProps> = ({
    data,
    fieldErrors,
    onClose,
    onUpdate,
    updateFieldErrors,
    isEditModalVisible
}) => {
    const [name, setName] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<string>('');
    const [description, setDescription] = useState<string>('');

    const dispatch = useDispatch();

    const category = useSelector((state: RootState) => getCategoryDetailsForEdit(state));
    useEffect(() => {
        if (data.id.length !== 1) dispatch(entityActions.fetchCategoryDetailsForEdit(data));
    }, [data, dispatch]);

    useEffect(() => {
        if (category?.data?.categoryName) {
            setName(category?.data?.categoryName);
        } else {
            setName('');
        }
        if (category?.data?.categoryOrder && hasValue(category?.data?.categoryOrder)) {
            setSortOrder(category?.data?.categoryOrder.toString());
        } else {
            setSortOrder('');
        }
        if (category?.data?.description && hasValue(category?.data?.description)) {
            setDescription(category?.data?.description);
        } else {
            setDescription('');
        }
    }, [category]);

    const handleUpdate = () => {
        let errors = fieldErrors;
        if (!name.trim()) {
            errors = addValidationError(errors, CategoryField.Name, SUB_CATEGORY_NAME_EMPTY_ERROR);
        }
        if (!sortOrder) {
            errors = addValidationError(
                errors,
                CategoryField.SortOrder,
                SUB_CATEGORY_ORDER_EMPTY_ERROR
            );
        }
        if (errors.length === 0) {
            const updatedData: CategoryDTO = {
                categoryId: data.id,
                categoryName: name.trim(),
                categoryOrder: Number(sortOrder),
                description: description
            };
            onUpdate(updatedData);
        }
        updateFieldErrors(errors);
    };

    const handleDescription = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Description);
        if (validateMaxLength(event.target.value, SUB_CATEGORY_DESCRIPTION_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                CategoryField.Description,
                SUB_CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR
            );
        }
        if (!validateInputString(event.target.value)) {
            errors = addValidationError(
                errors,
                CategoryField.Description,
                SUB_CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR
            );
        }
        setDescription(event.target.value);
        updateFieldErrors(errors);
    };

    const handleNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Name);
        if (event.target.value.trim().length === 0) {
            errors = addValidationError(errors, CategoryField.Name, SUB_CATEGORY_NAME_EMPTY_ERROR);
        }
        if (validateMaxLength(event.target.value, SUB_CATEGORY_NAME_MAX_LENGTH)) {
            errors = addValidationError(
                errors,
                CategoryField.Name,
                SUB_CATEGORY_NAME_MAX_LENGTH_ERROR
            );
        }
        if (!validateInputString(event.target.value)) {
            errors = addValidationError(
                errors,
                CategoryField.Name,
                SUB_CATEGORY_NAME_INVALID_CHAR_ERROR
            );
        }
        setName(event.target.value);
        updateFieldErrors(errors);
    };

    const handleSortOrderChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.SortOrder);
        if (event.target.value !== '') {
            if (!validateNumbers(event.target.value)) {
                errors = addValidationError(
                    errors,
                    CategoryField.SortOrder,
                    SUB_CATEGORY_ORDER_INVALID_ERROR
                );
            } else {
                if (Number(event.target.value) === 0) {
                    errors = addValidationError(
                        errors,
                        CategoryField.SortOrder,
                        SUB_CATEGORY_ORDER_GREATER_THAN_ZERO
                    );
                } else if (Number(event.target.value) > SUB_CATEGORY_ORDER_MAX_LENGTH) {
                    errors = addValidationError(
                        errors,
                        CategoryField.SortOrder,
                        SUB_CATEGORY_ORDER_MAX_LENGTH_ERROR
                    );
                }
            }
        } else {
            errors = addValidationError(
                errors,
                CategoryField.SortOrder,
                SUB_CATEGORY_ORDER_EMPTY_ERROR
            );
        }
        updateFieldErrors(errors);
        setSortOrder(event.target.value);
    };

    const headerElement = (
        <header className="sub-category-edit-header-name-wrapper">
            Editing sub-category:{' '}
            <span className="sub-category-edit-header-name" title={category.data?.categoryName}>
                {category.data?.categoryName ? category.data.categoryName : ''}
            </span>
        </header>
    );

    return (
        <Modal show={isEditModalVisible} onClose={onClose} style={{ width: '51vw' }}>
            <ModalHeader className="sub-category-edit-header">{headerElement}</ModalHeader>
            <ModalBody className="sub-category-edit-body">
                <div className="sub-category-top-section">
                    <FormField className="sub-category-form-field">
                        <Label text="Name (required)"></Label>
                        <Input
                            type={InputType.TEXT}
                            value={name}
                            onChange={handleNameChange}
                            onBlur={handleNameChange}
                        />
                        <FieldErrorList field={CategoryField.Name} errors={fieldErrors} />
                    </FormField>
                    <FormField className="sub-category-form-field-right">
                        <Label text="Sort Order (required)"></Label>
                        <Input
                            type={InputType.TEXT}
                            value={sortOrder}
                            onChange={handleSortOrderChange}
                            onBlur={handleSortOrderChange}
                        />
                        <FieldErrorList field={CategoryField.SortOrder} errors={fieldErrors} />
                    </FormField>
                </div>
                <FormField className="sub-category-form-field">
                    <Label text="Description"></Label>
                    <TextArea value={description} onChange={handleDescription} />
                    <FieldErrorList field={CategoryField.Description} errors={fieldErrors} />
                </FormField>
            </ModalBody>
            <ModalFooter className="sub-category-edit-footer">
                <Button
                    variant={ButtonVariant.Primary}
                    onClick={handleUpdate}
                    disabled={fieldErrors.length > 0}>
                    {ACTION_ITEMS.UPDATE_BUTTON}
                </Button>
                <Button variant={ButtonVariant.Secondary} onClick={onClose}>
                    {ACTION_ITEMS.CANCEL_BUTTON}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default SubCategoryEdit;
